import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppSidebar } from "@/components/sidebar/app-sidebar";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { listCampaigns, PaginatedCampaignsResponse } from "@/services/campaignsApi";

export default function CampaignsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<PaginatedCampaignsResponse | null>(null);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await listCampaigns(navigate, { page, size, status: statusFilter || undefined });
        setData(res);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Erreur inconnue");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, page, size, statusFilter]);

  const canPrev = page > 1;
  const canNext = data ? page < data.pages : false;

  const statusBadge = (status: string | null | undefined) => {
    const colors: Record<string, string> = {
      "en cours": "bg-green-100 text-green-800",
      "draft": "bg-yellow-100 text-yellow-800",
      "annulé": "bg-red-100 text-red-800",
      "annulée": "bg-red-100 text-red-800",
    };
    const s = (status || "").toLowerCase();
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[s] || "bg-gray-100 text-gray-800"}`}>
        {status || "-"}
      </span>
    );
  };

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <h1 className="text-sm font-medium">Campagnes</h1>
          </div>
        </header>

        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Liste des campagnes</h2>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Statut</label>
                <select className="border rounded px-2 py-1 text-sm" value={statusFilter} onChange={(e) => { setPage(1); setStatusFilter(e.target.value); }}>
                  <option value="">Tous</option>
                  <option value="en cours">En cours</option>
                  <option value="draft">Draft</option>
                  <option value="annulé">Annule</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Taille page</label>
                <select className="border rounded px-2 py-1 text-sm" value={size} onChange={(e) => { setPage(1); setSize(parseInt(e.target.value, 10)); }}>
                  {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            </div>
          </div>

          {error && <div className="text-red-600 text-sm">{error}</div>}
          {loading && <div className="text-gray-500 text-sm">Chargement...</div>}

          <div className="overflow-auto border rounded">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left">ID</th>
                  <th className="px-3 py-2 text-left">Nom</th>
                  <th className="px-3 py-2 text-left">Statut</th>
                  <th className="px-3 py-2 text-left">Subscription</th>
                  <th className="px-3 py-2 text-left">Candidats</th>
                  <th className="px-3 py-2 text-left">Politesse</th>
                  <th className="px-3 py-2 text-left">Cree le</th>
                </tr>
              </thead>
              <tbody>
                {data?.items?.map((c) => (
                  <tr key={c.id} className="border-t">
                    <td className="px-3 py-2">{c.id}</td>
                    <td className="px-3 py-2 font-medium">{c.name}</td>
                    <td className="px-3 py-2">{statusBadge(c.status)}</td>
                    <td className="px-3 py-2">{c.subscription_id ?? "-"}</td>
                    <td className="px-3 py-2">{c.candidate_count ?? 0}</td>
                    <td className="px-3 py-2">{c.politeness ?? "-"}</td>
                    <td className="px-3 py-2">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "-"}</td>
                  </tr>
                ))}
                {!data?.items?.length && !loading && (
                  <tr><td className="px-3 py-4 text-center text-gray-500" colSpan={7}>Aucune campagne</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">Page {data?.page ?? page} / {data?.pages ?? 1} — {data?.total ?? 0} elements</div>
            <div className="flex gap-2">
              <Button variant="outline" disabled={!canPrev} onClick={() => setPage((p) => Math.max(1, p - 1))}>Precedent</Button>
              <Button variant="outline" disabled={!canNext} onClick={() => setPage((p) => p + 1)}>Suivant</Button>
            </div>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
